/**
 * Dossier KYB (personne morale) : cycle de vie, instruction à quatre yeux et décision.
 * Le back porte toutes les règles (états, gardes, séparation des tâches) : ces types ne font
 * que décrire ce qu'il renvoie, le front n'en recalcule rien.
 */

export type KybCaseStatus =
  | "OPEN"
  | "SOUMIS"
  | "EN_ATTENTE_CLIENT"
  | "APPROUVE"
  | "APPROUVE_AVEC_RESTRICTIONS"
  | "REJETE"
  | "MISE_EN_ATTENTE_REGLEMENTAIRE"
  | "SURVEILLANCE_RENFORCEE"
  | "CLOSED";

export type KybCbsOpeningState =
  | "OUVERTURE_CBS_EN_ATTENTE"
  | "OUVERTURE_CBS_POUSSEE"
  | "A3_NON_DISPONIBLE";

export type KybCbsPushStatus = "PENDING" | "RETRY" | "UNKNOWN" | "DONE" | "DEAD";

export type KybDecisionOption =
  | "APPROUVE"
  | "APPROUVE_AVEC_RESTRICTIONS"
  | "REJETE"
  | "MISE_EN_ATTENTE_REGLEMENTAIRE";

/** Prérequis bloquants d'une soumission (HTTP 400, liste dans `data`). */
export type KybSubmissionBlocker =
  | "BENEFICIAL_OWNER_MISSING"
  | "REGISTRATION_NOT_VERIFIED";

/** Gardes sur une approbation (HTTP 409 ou `eligibility`). */
export type KybDecisionBlocker =
  | "NOT_VALIDATED_L1"
  | "SCREENING_PENDING"
  | "SCREENING_HIT_UNQUALIFIED"
  | "SANCTIONS_CONFIRMED_MATCH"
  | "RCCM_NOT_VERIFIED"
  | "BENEFICIAL_OWNER_NOT_VERIFIED"
  | "EDD_IN_PROGRESS"
  | "EDD_REJECTED";

/** Vérification d'une personne liée : vraie quand tous ses rôles ont été confrontés à un acte. */
export interface KybPersonVerification {
  relatedPersonId: string;
  verified: boolean;
  verifiedBy?: string;
  verifiedAt?: string;
  /** Acte invoqué (statuts, registre des actionnaires...). */
  source?: string;
  /** Pièce déposée qui prouve l'acte. */
  proofDocumentId?: string;
}

/** Criblage sanctions et PEP de l'entreprise : fournisseur, référence, date, résultat. */
export interface KybEntityScreeningSummary {
  provider?: string;
  status?: string;
  outcome?: string;
  reference?: string;
  caseReference?: string;
  screenedAt?: string;
  reportFilename?: string;
}

/** Criblage d'une personne liée ; sans fournisseur ni résultat tant qu'elle n'a pas été criblée. */
export interface KybPersonScreeningSummary {
  relatedPersonId: string;
  displayName: string;
  provider?: string;
  status?: string;
  outcome?: string;
  detail?: string;
  screenedAt?: string;
  /** PEP ou SANCTION quand une correspondance est retenue. */
  nature?: "PEP" | "SANCTION" | null;
  /** Homonymes écartés par le fournisseur avant de conclure. */
  homonymsDiscarded?: number;
  /** Profil(s) retenus par le fournisseur : ce que l'analyste compare à la personne du dossier. */
  candidates?: KybScreeningCandidate[];
}

export interface KybScreeningSummary {
  entity?: KybEntityScreeningSummary | null;
  persons: KybPersonScreeningSummary[];
}

/** Un déterminant du risque de l'entreprise, évalué : niveau (RF faible, RM modéré, RE élevé) et raison lisible. */
export interface KybRiskFactor {
  code: string;
  label: string;
  level: "RF" | "RM" | "RE";
  points: number;
  weight: number;
  reason: string;
}

export interface KybRiskOverride {
  code: string;
  reason: string;
}

/** Risque d'une entreprise : score de 1 à 3, classe, facteurs et forçages ; HIGH exige un dossier EDD. */
export interface KybRisk {
  caseId: string;
  score: number;
  classification: "LOW" | "STANDARD" | "HIGH";
  factors: KybRiskFactor[];
  overrides: KybRiskOverride[];
  eddRequired: boolean;
  computedAt: string;
  /** Seuils paramétrés, en centièmes de point (166 = 1,66) : la jauge se découpe sur eux. */
  thresholdLowMax?: number;
  thresholdStandardMax?: number;
}

export interface KybCaseDetail {
  caseId: string;
  countryCode: string;
  legalFormCode: string;
  status: KybCaseStatus;
  version: number;
  createdBy?: string;
  createdAt?: string;
  submittedAt?: string;
  submittedBy?: string;
  /** Un bénéficiaire effectif sans rôle probant vérifié : décision finale bloquée tant que non résolu. */
  beNonVerifie: boolean;
  /** Score d'opacité au seuil de recommandation d'examen renforcé. */
  structureOpaque: boolean;
  readyForValidation: boolean;
  /** Un facteur de risque élevé (pays à risque, correspondance confirmée, structure opaque) impose un dossier EDD avant d'approuver. */
  eddRequired?: boolean;
  /** Risque décidé par la validation L2 de l'EDD : MAINTAINED, UPGRADED ou DOWNGRADED. */
  eddRiskAssessment?: string;
  slaPausedAt?: string;
  slaResumedAt?: string;
  cbsOpeningState?: KybCbsOpeningState;
}

export interface KybSubmissionResult {
  caseId: string;
  status: KybCaseStatus;
  alreadySubmitted: boolean;
  beNonVerifie: boolean;
  structureOpaque: boolean;
  missingMandatoryDocuments: number;
  submittedAt?: string;
  submittedBy?: string;
}

/** Une soumission aboutit, ou le back liste les prérequis manquants (400) sans rien changer. */
/** Une condition de la soumission : remplie ou non, bloquante ou signalement seulement. */
export interface KybSubmissionPrerequisite {
  code: "BENEFICIAL_OWNER" | "REGISTRATION_VERIFIED" | "BENEFICIAL_OWNERS_VERIFIED";
  met: boolean;
  blocking: boolean;
}

/** Ce qu'il reste à faire avant de soumettre, lu sans rien exécuter. */
export interface KybSubmissionReadiness {
  status: KybCaseStatus;
  /** La soumission serait acceptée maintenant. */
  submittable: boolean;
  /** Le dossier est déjà entre les mains de la conformité ou décidé. */
  alreadySubmitted: boolean;
  items: KybSubmissionPrerequisite[];
}

export type KybSubmissionOutcome =
  | { outcome: "submitted"; result: KybSubmissionResult }
  | { outcome: "blocked"; blockers: KybSubmissionBlocker[] };

export interface KybInstructionState {
  caseId: string;
  status: KybCaseStatus;
  readyForValidation: boolean;
  readyBy?: string;
  slaPausedAt?: string;
  slaResumedAt?: string;
}

/** Où en est l'instruction d'un dossier : l'état que tous les volets de l'écran racontent de la même façon. */
export interface KybInstructionSummary {
  assignee?: string;
  noteCount: number;
  hitConclusionCount: number;
  readyBy?: string;
  readyAt?: string;
  l1ValidatedBy?: string;
  l1ValidatedAt?: string;
}

/** Rapport d'un analyste à l'agence du dossier : la sienne, le siège (sans agence), ou une autre. */
export type KybAnalystScope = "CASE_BRANCH" | "HEAD_OFFICE" | "OTHER_BRANCH";

/** Un analyste habilité à instruire, à qui l'instruction peut être assignée. */
export interface KybAssignableAnalyst {
  login: string;
  fullName: string;
  email?: string;
  branchCode?: string;
  branchName?: string;
  scope: KybAnalystScope;
}

export interface KybAssignableAnalysts {
  /** Agence qui a ouvert le dossier ; absente si le dossier n'en porte pas. */
  caseBranchCode?: string;
  caseBranchName?: string;
  analysts: KybAssignableAnalyst[];
}

/** Une pièce du catalogue paramétrable des pièces complémentaires que l'instruction peut réclamer. */
export interface KybComplementDocumentType {
  code: string;
  label: string;
  /** Une pièce n'est jamais supprimée : désactivée, elle n'est plus proposée mais reste lisible dans les demandes émises. */
  active: boolean;
  sortOrder: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface KybComplementRequestInput {
  requiredPieces: string[];
  message?: string;
}

export interface KybValidationInput {
  comment: string;
  /** Requis seulement si l'authentification forte est activée côté back. */
  otpCode?: string;
}

export interface KybValidationResult {
  caseId: string;
  status: KybCaseStatus;
  validatedBy: string;
  validatedAt?: string;
  alreadyValidated: boolean;
}

export interface KybMfaChallenge {
  /** Adresse masquée à laquelle le code a été envoyé (le code n'est jamais renvoyé). */
  sentTo: string;
  ttlMinutes: number;
  /** Renseigné seulement en environnement de test : aucun mail réel n'est parti, c'est le code à saisir. */
  testCode?: string;
}

export interface KybDecisionBlockerItem {
  code: KybDecisionBlocker;
  label: string;
}

export interface KybDecisionEligibility {
  blockers: KybDecisionBlockerItem[];
  allowedOptions: KybDecisionOption[];
}

export interface KybCaseDecisionInput {
  decision: KybDecisionOption;
  justification: string;
  /** Obligatoire pour `APPROUVE_AVEC_RESTRICTIONS`. */
  restrictions?: string;
  otpCode?: string;
}

export interface KybCaseDecisionResult {
  caseId: string;
  status: KybCaseStatus;
  decision: KybDecisionOption;
  decisionVersion: number;
  cbsOpeningState?: KybCbsOpeningState;
  decidedBy: string;
  decidedAt?: string;
}

/** Une décision est enregistrée, ou une garde interdit l'approbation (409) avec la liste des codes. */
export type KybCaseDecisionOutcome =
  | { outcome: "decided"; result: KybCaseDecisionResult }
  | { outcome: "blocked"; blockers: KybDecisionBlocker[] };

export interface KybCbsOpening {
  decisionVersion: number;
  status: KybCbsPushStatus;
  attempts: number;
  nextAttemptAt?: string;
  lastError?: string;
  cbsReference?: string;
  pushedAt?: string;
}

export interface KybCaseAuditEntry {
  entryId: string;
  fieldCode: string;
  fieldLabel: string;
  oldValue?: string;
  newValue?: string;
  critical: boolean;
  justification?: string;
  source: string;
  sourceLabel: string;
  actor: string;
  changedAt: string;
}

/** Filtres de l'historique d'un dossier, appliqués par le back. */
export interface KybAuditFilters {
  fieldCode?: string;
  actor?: string;
  /** `KYB` (action décidée ici) ou `RIC` (changement reçu du référentiel client). */
  source?: "KYB" | "RIC";
  /** Bornes de période au format `AAAA-MM-JJ`, incluses. */
  from?: string;
  to?: string;
}

/** Un champ que le journal sait tracer : il alimente la liste déroulante du filtre. */
export interface KybAuditFieldOption {
  code: string;
  label: string;
  critical: boolean;
}

export interface KybCaseTimeline {
  entries: KybCaseAuditEntry[];
  total: number;
  page: number;
  size: number;
}

/** Conclusion d'un analyste sur le hit de criblage d'une personne liée. */
export type KybScreeningQualificationDecision = "FALSE_POSITIVE" | "CONFIRMED_MATCH";

/** Hit de criblage AML/PEP d'une personne liée : à qualifier, confirmé, ou déjà tranché par un analyste. */
/** Identifiant du hit de l'entreprise elle-même dans la liste des hits : elle n'a pas d'identifiant de personne liée. */
export const KYB_ENTITY_HIT_ID = "entity";

/** Correspondance candidate rendue par le fournisseur : l'analyste désigne celle qui décrit la personne. */
export interface KybScreeningCandidate {
  /** Référence chez le fournisseur ; c'est elle qu'on renvoie pour désigner le candidat. */
  reference: string;
  fullName: string;
  birthDate?: string;
  nationality?: string;
  /** Score de correspondance, en pourcentage. */
  matchScore?: number;
  listTypes: string[];
  pep: boolean;
  /** Une sanction interdit l'approbation, même avec un EDD approuvé. */
  sanction: boolean;
  /** Catégorie du profil chez le fournisseur. */
  category?: string;
  residenceCountry?: string;
  placeOfBirth?: string;
  /** Personnes associées avec leur lien (parent, conjoint...). */
  associates: string[];
  /** Sources citées par le fournisseur. */
  sources: string[];
  /** Le candidat que le fournisseur proposait comme le plus proche : une suggestion, jamais une désignation. */
  suggested: boolean;
}

export interface KybScreeningHit {
  /** Identifiant de la personne liée, ou {@link KYB_ENTITY_HIT_ID} pour le hit de l'entreprise elle-même. */
  relatedPersonId: string;
  displayName: string;
  /** Société interposée plutôt que personne physique. */
  legalParty: boolean;
  /** Hit de l'entreprise du dossier elle-même, et non d'une personne liée. */
  entity?: boolean;
  /** `TO_QUALIFY` (à trancher), `MATCH` (confirmé) ou `NO_MATCH` (faux positif écarté). */
  outcome: string;
  provider?: string;
  /** Détail du dernier criblage ; pour une conclusion d'analyste, la conclusion, son auteur et sa justification. */
  detail?: string;
  screenedAt?: string;
  qualified: boolean;
  /** Correspondances à départager, pour un hit à qualifier ; absentes d'un ancien back. */
  candidates?: KybScreeningCandidate[];
}

export interface KybScreeningQualificationInput {
  decision: KybScreeningQualificationDecision;
  /** 10 à 1000 caractères, obligatoire. */
  justification: string;
  /** Correspondance qui décrit la personne ; obligatoire pour une confirmation quand le hit porte des candidats. */
  candidateReference?: string;
}

/** Poids (en %, somme 100) et seuils (en centièmes de point) de la règle de risque d'une entreprise. */
export interface KybRiskConfig {
  weightCountry: number;
  weightSector: number;
  weightStructure: number;
  weightOwnersCountry: number;
  weightScreening: number;
  thresholdLowMax: number;
  thresholdStandardMax: number;
}

export interface KybRiskConfigChange {
  modifiedAt: string;
  modifiedBy: string;
  reason: string;
}

export interface KybRiskConfigView {
  config: KybRiskConfig;
  history: KybRiskConfigChange[];
}

/** Risque figé à la décision : ce qui a fondé la décision, même si le calcul courant a changé depuis. */
export interface KybRiskSnapshot {
  takenAt: string;
  moment: string;
  decision?: string;
  takenBy?: string;
  score: number;
  classification: "LOW" | "STANDARD" | "HIGH";
  eddRequired: boolean;
  /** `déterminant:niveau:poids:raison` séparés par `|`, forçages après `||` (`facteur:raison`). */
  detail: string;
}

/** Une pièce réclamée au client et son état : reçue, en attente de dépôt, ou saisie à la main (à vérifier par l'analyste). */
export interface KybComplementPiece {
  code?: string;
  label: string;
  free: boolean;
  received: boolean;
}

/** Une demande de pièces faite au client d'un dossier. */
export interface KybComplementRequest {
  requestId: string;
  message?: string;
  requestedBy?: string;
  requestedByName?: string;
  requestedByBranch?: string;
  requestedAt: string;
  pieces: KybComplementPiece[];
}
